import { useRef, useState } from 'react';
import { AnchoredPanel } from '../../components/AnchoredPanel.tsx';
import { Button, Icons } from '../../components/ui.tsx';
import { useSheetCreation } from './useSheetCreation.ts';

/**
 * "New sheet", with templates and import behind the arrow.
 *
 * The Drive's toolbar uses the single New menu instead; this split button
 * remains for the dashboard and for the empty-state prompt, where there is no
 * toolbar to hang a menu on. All of them call the same useSheetCreation, so
 * the flows cannot drift apart.
 */
export function NewSheetButton({
  folderId,
  onError,
}: {
  folderId: string | null;
  onError(message: string): void;
}) {
  const [open, setOpen] = useState(false);
  // What the menu is measured from, so it can be drawn against the window
  // rather than against this button's corner. See AnchoredPanel.
  const group = useRef<HTMLDivElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const { templates, createFrom: pickLocation, importFiles } = useSheetCreation(folderId, onError);

  return (
    <div className="relative" ref={group}>
      <div className="flex">
        <Button
          icon={<Icons.Plus />}
          onClick={() => pickLocation({ title: 'New sheet', suggested: 'Untitled sheet' })}
        >
          New sheet
        </Button>
        <button
          type="button"
          aria-label="New from template or import"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="ml-1 h-control rounded-md border border-border-input px-2 text-body-sm text-body hover:bg-sunken"
        >
          ▾
        </button>
      </div>

      <input
        ref={fileInput}
        type="file"
        multiple
        accept=".xlsx,.xlsm,.xls,.csv,.tsv,.ods"
        className="hidden"
        onChange={(event) => {
          const chosen = Array.from(event.target.files ?? []);
          event.target.value = '';
          void importFiles(chosen);
        }}
      />

      {open ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <AnchoredPanel trigger={group} width={320}>
            {(templates.data ?? []).map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  pickLocation({
                    title: `New ${template.name.toLowerCase()}`,
                    suggested: template.id === 'blank' ? 'Untitled sheet' : template.name,
                    template: template.id,
                  });
                }}
                className="block w-full px-3 py-2 text-left hover:bg-sunken"
              >
                <span className="block text-body-sm font-medium text-ink">{template.name}</span>
                <span className="block text-micro text-muted">{template.description}</span>
              </button>
            ))}

            <div className="my-1 h-px bg-[var(--border)]" />

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                fileInput.current?.click();
              }}
              className="block w-full px-3 py-2 text-left hover:bg-sunken"
            >
              <span className="block text-body-sm font-medium text-ink">Import as a sheet…</span>
              <span className="block text-micro text-muted">
                Opens .xlsx, .xls, .csv or .ods files as editable sheets in the Drive. Choose
                several at once and they land in this folder under their own names.
              </span>
            </button>
          </AnchoredPanel>
        </>
      ) : null}
    </div>
  );
}
